import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const TransferModal = ({ isOpen, onClose, ticket, onTransfer }) => {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!recipientEmail?.trim()) {
      newErrors.email = 'Recipient email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(recipientEmail)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!recipientName?.trim()) {
      newErrors.name = 'Recipient name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      await onTransfer({
        ticketId: ticket?.id,
        recipientEmail: recipientEmail?.trim(),
        recipientName: recipientName?.trim(),
        message: message?.trim()
      });
      
      // Reset form
      setRecipientEmail('');
      setRecipientName('');
      setMessage('');
      setErrors({});
      onClose();
    } catch (error) {
      setErrors({ submit: 'Failed to transfer ticket. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setRecipientEmail('');
      setRecipientName('');
      setMessage('');
      setErrors({});
      onClose();
    }
  };

  if (!isOpen || !ticket) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Transfer Ticket</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            iconName="X"
            disabled={isLoading}
          />
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Ticket Info */}
          <div className="bg-muted rounded-lg p-4 mb-6">
            <h3 className="font-medium text-foreground mb-1">{ticket?.eventName}</h3>
            <p className="text-sm text-muted-foreground mb-2">{ticket?.ticketType}</p>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Current Holder:</span>
              <span className="text-foreground">{ticket?.holderName}</span>
            </div>
          </div>

          {/* Transfer Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Recipient Email"
              type="email"
              placeholder="Enter recipient's email address"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e?.target?.value)}
              error={errors?.email}
              required
              disabled={isLoading}
            />

            <Input
              label="Recipient Name"
              type="text"
              placeholder="Enter recipient's full name"
              value={recipientName}
              onChange={(e) => setRecipientName(e?.target?.value)}
              error={errors?.name}
              required
              disabled={isLoading}
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Message (Optional)
              </label>
              <textarea
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                rows={3}
                placeholder="Add a personal message for the recipient..."
                value={message}
                onChange={(e) => setMessage(e?.target?.value)}
                disabled={isLoading}
              />
            </div>

            {errors?.submit && (
              <div className="flex items-center space-x-2 text-error text-sm">
                <Icon name="AlertCircle" size={16} />
                <span>{errors?.submit}</span>
              </div>
            )}

            {/* Transfer Notice */}
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
                <div className="text-sm">
                  <p className="text-foreground font-medium mb-1">Important Notice</p>
                  <p className="text-muted-foreground">
                    Once transferred, you will no longer have access to this ticket. 
                    The recipient will receive an email with the new ticket details.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                loading={isLoading}
                iconName="Send"
                className="flex-1"
              >
                Transfer Ticket
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TransferModal;